import { useState } from "react";
import ConfirmDialog from "../ConfirmDialog.jsx";
import Field from "../ui/Field.jsx";
import Panel from "../ui/Panel.jsx";
import Tabs from "../ui/Tabs.jsx";
import { ErrorText } from "../ui/Notice.jsx";
import { FIELD } from "../ui/styles.js";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";
import {
    ESCALATABLE_ROLE_LABEL,
    ESCALATION_STATES,
    WINDOW_PRESETS,
} from "../../lib/escalationClient.js";
import { displayState, formatAgo, formatWindow, shortId } from "../../lib/escalationLabels.js";
import { Action, Countdown, StateChip, TargetChip } from "./EscalationParts.jsx";

const NOTE = "text-[12px] leading-[1.45] text-[color:var(--muted)]";

// Approving grants the requested window or a shorter one, with an optional
// note for the requester.
function ApproveDialog({ request, requester, busy, onConfirm, onCancel }) {
    const [seconds, setSeconds] = useState(request.requestedSeconds);
    const [note, setNote] = useState("");
    const options = [
        { id: request.requestedSeconds, label: `${formatWindow(request.requestedSeconds)} (requested)` },
        ...WINDOW_PRESETS.filter((preset) => preset.seconds < request.requestedSeconds).map((preset) => ({ id: preset.seconds, label: preset.label })),
    ];
    return (
        <ConfirmDialog
            open
            analyticsId={DIALOG.ESCALATION_DECIDE}
            eyebrow={requester}
            title="Approve access"
            body={(
                <div className="grid gap-[14px]">
                    <Field as="div" label="Access duration" hint="Approve the requested duration or choose a shorter one.">
                        <Tabs mode="toggle" label="Access duration" items={options} value={seconds} onChange={setSeconds} className="flex-wrap self-start" />
                    </Field>
                    <Field label="Decision note (optional)">
                        <input
                            type="text"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            aria-label="Decision note"
                            className={FIELD}
                        />
                    </Field>
                </div>
            )}
            confirmLabel={busy ? "Approving..." : `Approve ${formatWindow(seconds)}`}
            confirmDisabled={busy}
            onConfirm={() => onConfirm({ approve: true, grantedSeconds: seconds, note })}
            onCancel={onCancel}
        />
    );
}

function DenyDialog({ requester, busy, onConfirm, onCancel }) {
    const [note, setNote] = useState("");
    return (
        <ConfirmDialog
            open
            analyticsId={DIALOG.ESCALATION_DECIDE}
            eyebrow={requester}
            title="Deny request"
            body={(
                <Field label="Reason for denial" hint="The requester sees this.">
                    <input
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        aria-label="Denial note"
                        className={FIELD}
                    />
                </Field>
            )}
            confirmLabel={busy ? "Denying..." : "Deny request"}
            confirmDisabled={busy}
            destructive
            onConfirm={() => onConfirm({ approve: false, note })}
            onCancel={onCancel}
        />
    );
}

export default function EscalationCard({
    request,
    endpointNames,
    subjectNames,
    isOwn,
    canDecide,
    busy = false,
    error = null,
    onDecide,
    onCancel,
    onRevoke,
}) {
    // The decision being confirmed: "approve" | "deny" | "revoke".
    const [asking, setAsking] = useState(null);
    const state = displayState(request);
    const live = state === ESCALATION_STATES.APPROVED;
    const pending = state === ESCALATION_STATES.PENDING;
    const approvable = pending && Boolean(request.requestedRoleId);
    const requester = isOwn ? "You" : request.requesterDisplay || shortId(request.requesterUserId);
    const decide = (decision) => { setAsking(null); onDecide?.(decision); };

    const actions = (pending && (isOwn || canDecide)) || (live && canDecide) ? (
        <>
            {pending && isOwn ? (
                <Action disabled={busy} onClick={onCancel}>Withdraw</Action>
            ) : null}
            {pending && canDecide ? (
                <>
                    <Action disabled={busy} onClick={() => setAsking("deny")}>Deny</Action>
                    <Action tone="primary" disabled={busy || !approvable} onClick={() => setAsking("approve")}>Approve</Action>
                </>
            ) : null}
            {live && canDecide ? (
                <Action tone="danger" disabled={busy} onClick={() => setAsking("revoke")}>Revoke access</Action>
            ) : null}
        </>
    ) : null;

    return (
        <Panel
            as="article"
            title={<span title={request.requesterUserId}>{requester}</span>}
            meta={(
                <span className="flex flex-wrap items-center gap-x-[12px] gap-y-[3px]">
                    <StateChip state={state} />
                    {live && request.expiresAt ? <Countdown expiresAt={request.expiresAt} /> : null}
                    <span>{formatAgo(request.createdAt)}</span>
                </span>
            )}
            actions={actions}
            className="gap-[10px]"
        >
            <TargetChip
                target={request.target}
                endpointNames={endpointNames}
                subjectNames={subjectNames}
            />

            <p className="m-0 whitespace-pre-wrap break-words text-[13px] leading-[1.5] text-[color:var(--ink)]">
                {request.justification}
            </p>

            <div className={`${NOTE} flex flex-wrap gap-x-[16px] gap-y-[2px]`}>
                <span>
                    {request.requestedRoleId ? ESCALATABLE_ROLE_LABEL : "Missing role: cannot approve"}
                </span>
                <span>{formatWindow(request.requestedSeconds)} requested</span>
                {request.expiresAt && !live ? (
                    <span>Expiry: {request.expiresAt.toLocaleString()}</span>
                ) : null}
            </div>

            {request.decisionNote ? (
                <p className={`${NOTE} m-0`}>
                    Decision note: <span className="text-[color:var(--ink)]">{request.decisionNote}</span>
                </p>
            ) : null}

            <ErrorText>{error}</ErrorText>

            {asking === "approve" ? (
                <ApproveDialog request={request} requester={requester} busy={busy} onConfirm={decide} onCancel={() => setAsking(null)} />
            ) : asking === "deny" ? (
                <DenyDialog requester={requester} busy={busy} onConfirm={decide} onCancel={() => setAsking(null)} />
            ) : asking === "revoke" ? (
                <ConfirmDialog
                    open
                    analyticsId={DIALOG.ESCALATION_DECIDE}
                    eyebrow={requester}
                    title="Revoke access?"
                    body="Their temporary access ends now, before the approved window runs out. The audit trail keeps what they read."
                    confirmLabel="Revoke"
                    destructive
                    onConfirm={() => { setAsking(null); onRevoke?.(); }}
                    onCancel={() => setAsking(null)}
                />
            ) : null}
        </Panel>
    );
}
